import { OpenAI } from "openai";

interface OpenAIConfig {
  apiKey: string;
}

export class OpenAIService {
  private client: OpenAI;

  constructor(config: OpenAIConfig) {
    this.client = new OpenAI({
      apiKey: config.apiKey,
      dangerouslyAllowBrowser: true, // Enable browser usage
    });
  }

  private async convertAudioFormat(audioBlob: Blob): Promise<Blob> {
    return new Promise(async (resolve, reject) => {
      try {
        const audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        const arrayBuffer = await audioBlob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const offlineContext = new OfflineAudioContext({
          numberOfChannels: 1, // Mono
          length: Math.ceil(audioBuffer.duration * 16000),
          sampleRate: 16000, // Required sample rate for OpenAI models
        });

        const source = offlineContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(offlineContext.destination);
        source.start();

        const renderedBuffer = await offlineContext.startRendering();
        const wavBlob = await new Promise<Blob>((resolve) => {
          const channels = 1; // Mono
          const length = renderedBuffer.length * 2; // 16-bit samples
          const buffer = new ArrayBuffer(44 + length);
          const view = new DataView(buffer);

          // Write WAV header
          const writeString = (offset: number, string: string) => {
            for (let i = 0; i < string.length; i++) {
              view.setUint8(offset + i, string.charCodeAt(i));
            }
          };

          writeString(0, "RIFF");
          view.setUint32(4, 36 + length, true);
          writeString(8, "WAVE");
          writeString(12, "fmt ");
          view.setUint32(16, 16, true);
          view.setUint16(20, 1, true); // PCM format
          view.setUint16(22, channels, true);
          view.setUint32(24, 16000, true); // Sample rate
          view.setUint32(28, 16000 * 2, true); // Byte rate
          view.setUint16(32, 2, true); // Block align
          view.setUint16(34, 16, true); // Bits per sample
          writeString(36, "data");
          view.setUint32(40, length, true);

          // Write audio data
          const channel = renderedBuffer.getChannelData(0);
          let offset = 44;
          for (let i = 0; i < channel.length; i++) {
            const sample = Math.max(-1, Math.min(1, channel[i]));
            view.setInt16(
              offset,
              sample < 0 ? sample * 0x8000 : sample * 0x7fff,
              true
            );
            offset += 2;
          }

          resolve(new Blob([buffer], { type: "audio/wav" }));
        });

        resolve(wavBlob);
      } catch (error) {
        reject(error);
      }
    });
  }

  async transcribe(
    audioBlob: Blob,
    model: string = "whisper-1"
  ): Promise<{ text: string; duration: number }> {
    const startTime = Date.now();

    try {
      // Always convert to ensure format compatibility
      const convertedAudio = await this.convertAudioFormat(audioBlob);
      const file = new File([convertedAudio], "audio.wav", {
        type: "audio/wav",
      });

      const transcription = await this.client.audio.transcriptions.create({
        file,
        model,
        response_format: "json",
      });

      const duration = (Date.now() - startTime) / 1000;
      return {
        text: transcription.text,
        duration,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`OpenAI transcription error: ${error.message}`);
      }
      throw new Error("Unknown transcription error occurred");
    }
  }

  async translate(
    audioBlob: Blob,
    _targetLanguage: string = "en",
    model: string = "whisper-1"
  ): Promise<{ text: string; duration: number }> {
    const startTime = Date.now();

    try {
      // Always convert to ensure format compatibility
      const convertedAudio = await this.convertAudioFormat(audioBlob);
      const file = new File([convertedAudio], "audio.wav", {
        type: "audio/wav",
      });

      const translation = await this.client.audio.translations.create({
        file,
        model,
        response_format: "json",
        prompt: `Translate the following text to English only. Maintain the original meaning and tone. Only respond with the translation, no explanations or additional text.`,
      });

      const duration = (Date.now() - startTime) / 1000;
      return {
        text: translation.text,
        duration,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`OpenAI translation error: ${error.message}`);
      }
      throw new Error("Unknown translation error occurred");
    }
  }

  async translateText(
    text: string,
    targetLanguage: string = "en",
    model: string = "gpt-4o-mini"
  ): Promise<{ text: string; duration: number }> {
    const startTime = Date.now();

    try {
      const completion = await this.client.chat.completions.create({
        model,
        messages: [
          {
            role: "system",
            content: `You are a professional translator. Translate the following text to ${targetLanguage}. Maintain the original meaning and tone. Only respond with the translation, no explanations or additional text.`,
          },
          {
            role: "user",
            content: text,
          },
        ],
        temperature: 0.3,
      });

      const duration = (Date.now() - startTime) / 1000;
      return {
        text: completion.choices[0]?.message?.content || "",
        duration,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`OpenAI text translation error: ${error.message}`);
      }
      throw new Error("Unknown text translation error occurred");
    }
  }
}
